import React, { useState } from 'react';
import { Box, Typography, Button, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import logging from 'utils/logging';

const log = logging.getLogger('WhatWentWrong');

// Firestore Timestamps and Date instances must be stringified before render
// or React will throw "Objects are not valid as a React child".
const toDisplayString = (val) => {
  if (val == null) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val?.toDate === 'function') {
    try { return val.toDate().toISOString(); } catch { return ''; }
  }
  if (val instanceof Date) return val.toISOString();
  return '';
};

const Disclosure = styled(Button)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.75rem',
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#888',
  border: '1px solid rgba(0,0,0,0.12)',
  background: 'transparent',
  padding: '8px 14px',
  borderRadius: 4,
  fontWeight: 700,
});

const Panel = styled(Box)({
  marginTop: 10,
  padding: '12px 14px',
  background: '#fafafa',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 6,
});

const Meta = styled(Typography)({
  fontSize: '0.7rem',
  letterSpacing: '0.18em',
  color: '#888',
  textTransform: 'uppercase',
  fontWeight: 700,
  marginBottom: 4,
});

const Mono = styled(Box)({
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: '0.78rem',
  color: '#222',
  background: '#fff',
  border: '1px solid rgba(0,0,0,0.08)',
  borderRadius: 4,
  padding: '8px 10px',
  maxHeight: 200,
  overflowY: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

const CopyBtn = styled(Button)({
  fontFamily: 'Helvetica Inserat, Impact, sans-serif',
  fontSize: '0.7rem',
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  marginTop: 10,
});

const WhatWentWrong = ({ errorMessage, failedAt, failedPhase }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const failedAtDisplay = toDisplayString(failedAt);
  const errorDisplay = errorMessage || '(no error message recorded)';

  const block =
    `Phase: ${failedPhase}\n` +
    `Failed at: ${failedAtDisplay}\n` +
    `Error: ${errorDisplay}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(block);
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      log.warn('Clipboard write failed', e);
      setCopyFailed(true);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Disclosure onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide Details' : 'What went wrong?'}
      </Disclosure>
      <Collapse in={open} unmountOnExit>
        <Panel>
          <Meta>Phase</Meta>
          <Typography variant="body2" sx={{ mb: 1 }}>{failedPhase}</Typography>
          <Meta>Failed At</Meta>
          <Typography variant="body2" sx={{ mb: 1 }}>{failedAtDisplay || '—'}</Typography>
          <Meta>Error</Meta>
          <Mono>{errorDisplay}</Mono>
          <CopyBtn variant="outlined" size="small" onClick={handleCopy}>
            {copied ? 'Copied' : copyFailed ? 'Copy unavailable' : 'Copy Details'}
          </CopyBtn>
        </Panel>
      </Collapse>
    </Box>
  );
};

export default WhatWentWrong;
